import React, { useState, useEffect } from 'react';
import {
  Home, ShoppingCart, Wrench, Bell, User, Menu, X, LogOut,
  Building2, Smartphone, DollarSign, Server, Settings, AlertTriangle,
  MapPin, Truck, Printer, CreditCard, Cloud, Wifi, Scale, Network,
  Database, Shield, Activity, Mail, Calendar, LayoutDashboard, Package,
  FileText, FileCheck, ChevronDown, ChevronRight, Users,
  HardDrive, Phone, Monitor, Package2, Cable, BarChart3, TrendingUp, Megaphone, Search,
  Cpu, Laptop, Router, Camera, Video, Lock, Key, Folder, FolderOpen, Archive, Box, Boxes,
  ClipboardList, ClipboardCheck, ListTodo, CheckSquare, FileSpreadsheet,
  Receipt, Wallet, PiggyBank, CircleDollarSign, BadgeDollarSign, Banknote,
  Globe, Link, ExternalLink, Share2, MessageSquare, MessagesSquare, Send,
  UserPlus, UserMinus, UserCheck, UserCog, UsersRound, Contact, BookUser,
  Briefcase, Building, Store, Warehouse, Factory, Landmark,
  HelpCircle, Info, AlertCircle, XCircle, CheckCircle, Clock, Timer,
  Zap, Power, PlugZap, Battery, BatteryCharging, Sun, Moon, Lightbulb,
  Headphones, Speaker, Mic, Radio, Tv, Gamepad2, MousePointer, Keyboard,
  ScanLine, QrCode, Barcode, Tag, Tags, Bookmark, Star, Heart, Flag,
  Target, Crosshair, Compass, Navigation, Map, Route, Milestone, Signpost,
  Gauge, Thermometer, Droplet, Wind, Flame, Snowflake, Sparkles
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { apiService } from '../services/api';
import { menuItems } from '../data/menuData';
import { useMenuData } from '../hooks/useMenuData';
import MobileUpdateNotification from './MobileUpdateNotification';

interface MobileNavigationProps {
  currentPage: string;
  onNavigate: (page: string) => void;
}

type CategoryFilter = 'all' | 'zimmet' | 'devices' | 'service' | 'network' | 'settings';

const MobileNavigation: React.FC<MobileNavigationProps> = ({ currentPage, onNavigate }) => {
  const [showFullMenu, setShowFullMenu] = useState(false);
  const [systemTitle, setSystemTitle] = useState('IT Toolbox');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('all');

  const { user: currentUser, hasMenuPermission, hasRole, logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const { menuItems: dynamicMenuItems, loading: menuLoading, error } = useMenuData();

  useEffect(() => {
    loadSystemSettings();

    const handleSystemSettingsUpdate = () => {
      loadSystemSettings();
    };

    window.addEventListener('systemSettingsUpdated', handleSystemSettingsUpdate);
    return () => {
      window.removeEventListener('systemSettingsUpdated', handleSystemSettingsUpdate);
    };
  }, []);

  const loadSystemSettings = async () => {
    try {
      const settings = await apiService.getSystemSettings();
      if (Array.isArray(settings)) {
        const titleSetting = settings.find((s: any) => s.setting_key === 'system_title');
        if (titleSetting && titleSetting.setting_value) {
          setSystemTitle(titleSetting.setting_value);
        }
      }
    } catch (error) {
      console.error('Error loading system settings:', error);
    }
  };

  // Icon mapping
  const iconComponents: { [key: string]: React.ComponentType<any> } = {
    LayoutDashboard, Settings, MapPin, Building2, Truck, Package,
    Smartphone, CreditCard, DollarSign, ShoppingCart, Wrench,
    Server, Cloud, Database, Network, Wifi, Scale, Shield, Users, User,
    Activity, Mail, AlertTriangle, Calendar, Printer, FileText, FileCheck, Bell,
    HardDrive, Phone, Monitor, Package2, Cable, Cpu, Laptop, Router,
    Camera, Video, Keyboard, MousePointer, Headphones, Speaker, Mic, Radio, Tv, Gamepad2,
    BarChart3, TrendingUp, Gauge,
    Megaphone, MessageSquare, MessagesSquare, Send,
    Search, Globe, Link, ExternalLink, Share2, Navigation, Map, Route, Compass,
    UserPlus, UserMinus, UserCheck, UserCog, UsersRound, Contact, BookUser,
    Briefcase, Building, Home, Store, Warehouse, Factory, Landmark,
    Folder, FolderOpen, Archive, Box, Boxes, ClipboardList, ClipboardCheck, ListTodo, CheckSquare, FileSpreadsheet,
    Receipt, Wallet, PiggyBank, CircleDollarSign, BadgeDollarSign, Banknote,
    Lock, Key,
    HelpCircle, Info, AlertCircle, XCircle, CheckCircle, Clock, Timer,
    Zap, Power, PlugZap, Battery, BatteryCharging, Sun, Moon, Lightbulb,
    ScanLine, QrCode, Barcode, Tag, Tags, Bookmark, Star, Heart, Flag, Target, Crosshair, Milestone, Signpost,
    Thermometer, Droplet, Wind, Flame, Snowflake
  };

  // Bottom Navigation Items - En kritik 5 ana yönlendirme
  const quickNavItems = [
    { id: 'dashboard', label: t('mobile.dashboard') || 'Ana Sayfa', icon: Home, route: '/' },
    { id: 'assignment-forms', label: 'Zimmetler', icon: FileText, route: '/assignment-forms' },
    { id: 'technical-service', label: t('mobile.service') || 'Servis', icon: Wrench, route: '/technical-service' },
    { id: 'search-trigger', label: 'Arama', icon: Search, action: 'search' },
    { id: 'menu', label: t('mobile.menu') || 'Menü', icon: Menu, action: 'menu' }
  ];

  // Mevcut menü verileri
  const currentMenuItems = Array.isArray(dynamicMenuItems) && dynamicMenuItems.length > 0 && !error 
    ? dynamicMenuItems 
    : menuItems;

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(existingId => existingId !== itemId)
        : [...prev, itemId]
    );
  };

  const handleNavigation = (pageId: string, href?: string) => {
    if (pageId === 'search-trigger') {
      window.dispatchEvent(new CustomEvent('openMobileSearch'));
      setShowFullMenu(false);
      return;
    }

    if (pageId === 'menu') {
      setShowFullMenu(true);
      return;
    }

    const targetPage = href ? href.replace(/^\//, '') : pageId.replace(/^\//, '');
    onNavigate(targetPage || 'dashboard');
    setShowFullMenu(false);
  };

  const isActive = (pageId: string, route?: string) => {
    const cleanCurrent = currentPage.replace(/^\//, '');
    const cleanTarget = pageId.replace(/^\//, '');
    const cleanRoute = route ? route.replace(/^\//, '') : '';
    return cleanCurrent === cleanTarget || (cleanRoute && cleanCurrent === cleanRoute);
  };

  // Filtering menu items by search & category
  const filterMenuItems = (items: any[]): any[] => {
    if (!Array.isArray(items)) return [];

    return items.filter(item => {
      if (!item) return false;

      const itemId = String(item.id || '').toLowerCase();
      const itemLabel = String(item.name || item.label || '').toLowerCase();
      const query = searchQuery.trim().toLowerCase();

      // Category filter check
      let categoryMatch = true;
      if (selectedCategory === 'zimmet') {
        categoryMatch = itemId.includes('assignment') || itemId.includes('document') || itemLabel.includes('zimmet') || itemLabel.includes('evrak');
      } else if (selectedCategory === 'devices') {
        categoryMatch = itemId.includes('computer') || itemId.includes('phone') || itemId.includes('equipment') || itemId.includes('printer') || itemId.includes('consumable') || itemId.includes('it_equipment') || itemLabel.includes('cihaz') || itemLabel.includes('donanım');
      } else if (selectedCategory === 'service') {
        categoryMatch = itemId.includes('technical') || itemId.includes('camera') || itemId.includes('damage') || itemId.includes('supplier') || itemLabel.includes('servis') || itemLabel.includes('arıza') || itemLabel.includes('kamera');
      } else if (selectedCategory === 'network') {
        categoryMatch = itemId.includes('network') || itemId.includes('switch') || itemId.includes('server') || itemId.includes('circuit') || itemId.includes('cabinet') || itemLabel.includes('ağ') || itemLabel.includes('sunucu');
      } else if (selectedCategory === 'settings') {
        categoryMatch = itemId.includes('setting') || itemId.includes('company') || itemId.includes('location') || itemId.includes('user') || itemId.includes('announcement') || itemLabel.includes('ayar') || itemLabel.includes('kullanıcı');
      }

      if (!categoryMatch && selectedCategory !== 'all') {
        // Also check if any child matches category
        const hasChildCategoryMatch = item.children && Array.isArray(item.children) && filterMenuItems(item.children).length > 0;
        if (!hasChildCategoryMatch) return false;
      }

      // Search query check
      if (query) {
        const directMatch = itemLabel.includes(query) || itemId.includes(query);
        const childMatch = item.children && Array.isArray(item.children) && item.children.some((c: any) => {
          const cLabel = String(c.name || c.label || '').toLowerCase();
          const cId = String(c.id || '').toLowerCase();
          return cLabel.includes(query) || cId.includes(query);
        });
        return directMatch || childMatch;
      }

      return true;
    });
  };

  const filteredMenuItems = filterMenuItems(currentMenuItems);

  const renderMenuItem = (item: any, level = 0) => {
    if (!item) return null;
    
    const itemId = String(item.id || '');
    const itemLabelRaw = String(item.name || item.label || '');
    const itemLabel = (language === 'en' && item.name_en) ? item.name_en : itemLabelRaw;
    const itemHref = item.route || item.href;
    const itemIcon = item.icon;

    const IconComponent = itemIcon && iconComponents[itemIcon as keyof typeof iconComponents] 
      ? iconComponents[itemIcon as keyof typeof iconComponents] 
      : Settings;
    
    const isSearchActive = searchQuery.trim().length > 0;
    const isExpanded = expandedItems.includes(itemId) || isSearchActive;
    const hasChildren = item.children && Array.isArray(item.children) && item.children.length > 0;
    const active = isActive(itemId, itemHref);

    const indentStyle = level > 0 ? { marginLeft: `${level * 12}px` } : {};

    return (
      <div key={itemId} className="mb-1">
        <div
          className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all ${
            active
              ? 'bg-indigo-600 text-white font-bold shadow-md shadow-indigo-200 dark:shadow-none'
              : 'bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-slate-800 dark:text-gray-200 hover:bg-slate-50 dark:hover:bg-gray-700'
          }`}
          style={indentStyle}
          onClick={() => {
            if (hasChildren) {
              toggleExpanded(itemId);
            } else if (itemHref) {
              handleNavigation(itemId, itemHref);
            }
          }}
        >
          <div className="flex items-center flex-1 min-w-0 pr-2">
            <div className={`p-2 rounded-lg mr-3 flex-shrink-0 ${active ? 'bg-white/20 text-white' : 'bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-gray-300'}`}>
              <IconComponent className="w-4 h-4" />
            </div>
            <span className="text-sm font-semibold truncate">{itemLabel}</span>
          </div>

          {hasChildren && (
            <div className={`p-1 rounded-md ${active ? 'text-white' : 'text-slate-400'}`}>
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </div>
          )}
        </div>
        
        {hasChildren && isExpanded && (
          <div className="mt-1.5 space-y-1 pl-2 border-l-2 border-indigo-200 dark:border-indigo-800 ml-3">
            {item.children
              .filter((child: any) => child)
              .map((child: any) => renderMenuItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <MobileUpdateNotification variant="banner" position="top" />

      {/* Bottom Navigation Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-t border-slate-200 dark:border-gray-800 z-50 md:hidden shadow-lg">
        <div className="grid grid-cols-5 h-16 max-w-md mx-auto">
          {quickNavItems.map((item) => {
            const IconComponent = item.icon;
            const active = item.route ? isActive(item.id, item.route) : false;
            
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNavigation(item.id, item.route)}
                className={`flex flex-col items-center justify-center space-y-1 transition-all active:scale-95 ${
                  active 
                    ? 'text-indigo-600 dark:text-indigo-400 font-bold' 
                    : 'text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-gray-200'
                }`}
              >
                <div className={`p-1.5 rounded-xl transition-colors ${active ? 'bg-indigo-50 dark:bg-indigo-950/60' : ''}`}>
                  <IconComponent className="w-5 h-5" />
                </div>
                <span className="text-[11px] leading-none font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Full Menu Overlay Drawer */}
      {showFullMenu && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 md:hidden flex flex-col justify-end animate-fadeIn">
          <div className="bg-slate-50 dark:bg-gray-900 rounded-t-3xl max-h-[92vh] flex flex-col w-full shadow-2xl border-t border-white/20 overflow-hidden">
            
            {/* Header */}
            <div className="p-4 bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
              <button
                type="button"
                className="flex items-center text-left flex-1 mr-2"
                onClick={() => handleNavigation('profile', '/profile')}
              >
                <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold mr-3 shadow-md">
                  {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : 'U'}
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 dark:text-gray-100 text-sm">{currentUser?.name || 'Kullanıcı'}</h3>
                  <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">{currentUser?.email || systemTitle}</p>
                </div>
              </button>

              <div className="flex items-center gap-2">
                {/* Language Switch */}
                <div className="flex items-center bg-slate-100 dark:bg-gray-700 rounded-lg p-0.5 border">
                  <button
                    type="button"
                    onClick={() => setLanguage('tr')}
                    className={`px-2 py-0.5 text-xs font-bold rounded ${language === 'tr' ? 'bg-white dark:bg-gray-600 text-indigo-600 shadow-xs' : 'text-slate-500'}`}
                  >
                    TR
                  </button>
                  <button
                    type="button"
                    onClick={() => setLanguage('en')}
                    className={`px-2 py-0.5 text-xs font-bold rounded ${language === 'en' ? 'bg-white dark:bg-gray-600 text-indigo-600 shadow-xs' : 'text-slate-500'}`}
                  >
                    EN
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setShowFullMenu(false)}
                  className="p-2 text-slate-400 hover:text-slate-700 dark:hover:text-gray-200 rounded-full hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Live Search Box */}
            <div className="p-3 bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 flex-shrink-0">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Menüde veya sayfalarda ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-8 py-2 text-sm bg-slate-100 dark:bg-gray-700 border-0 rounded-xl focus:ring-2 focus:ring-indigo-500 text-slate-900 dark:text-gray-100 placeholder-slate-400"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Quick Category Chips */}
              <div className="flex items-center gap-1.5 overflow-x-auto pt-2.5 pb-0.5 no-scrollbar text-xs">
                {[
                  { id: 'all', label: 'Tümü', icon: Sparkles },
                  { id: 'zimmet', label: 'Zimmet & Evrak', icon: FileText },
                  { id: 'devices', label: 'Cihazlar', icon: Smartphone },
                  { id: 'service', label: 'Servis & Kamera', icon: Wrench },
                  { id: 'network', label: 'Ağ & Sunucu', icon: Server },
                  { id: 'settings', label: 'Ayarlar', icon: Settings },
                ].map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setSelectedCategory(cat.id as CategoryFilter)}
                    className={`px-2.5 py-1 rounded-full font-bold whitespace-nowrap flex items-center gap-1 transition-all ${
                      selectedCategory === cat.id
                        ? 'bg-indigo-600 text-white shadow-xs'
                        : 'bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-gray-300 hover:bg-slate-200'
                    }`}
                  >
                    <cat.icon className="w-3 h-3" />
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Actions Shortcuts Grid */}
            <div className="p-3 bg-slate-100/70 dark:bg-gray-800/50 border-b border-slate-200 dark:border-gray-700 flex-shrink-0">
              <span className="text-[11px] font-bold uppercase text-slate-400 tracking-wider mb-2 block">Sık Kullanılan Hızlı Erişim</span>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => handleNavigation('assignment-forms', '/assignment-forms')}
                  className="p-2.5 bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 text-left hover:border-indigo-400 transition-all flex flex-col justify-between"
                >
                  <FileText className="w-5 h-5 text-indigo-600 mb-1" />
                  <span className="text-xs font-bold text-slate-800 dark:text-gray-100 leading-tight">Zimmet Formları</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleNavigation('technical-service', '/technical-service')}
                  className="p-2.5 bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 text-left hover:border-indigo-400 transition-all flex flex-col justify-between"
                >
                  <Wrench className="w-5 h-5 text-amber-600 mb-1" />
                  <span className="text-xs font-bold text-slate-800 dark:text-gray-100 leading-tight">Teknik Servis</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleNavigation('camera-faults', '/camera-faults')}
                  className="p-2.5 bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 text-left hover:border-indigo-400 transition-all flex flex-col justify-between"
                >
                  <Camera className="w-5 h-5 text-rose-600 mb-1" />
                  <span className="text-xs font-bold text-slate-800 dark:text-gray-100 leading-tight">Kamera Arızaları</span>
                </button>
              </div>
            </div>

            {/* Dynamic Menu Tree Scroll Container */}
            <div className="p-3 overflow-y-auto flex-1 max-h-[50vh]">
              {menuLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : filteredMenuItems.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Search className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm font-medium">Aramanıza uygun menü öğesi bulunamadı.</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredMenuItems.map(item => renderMenuItem(item))}
                </div>
              )}
            </div>

            {/* Drawer Footer Actions */}
            <div className="p-3 bg-white dark:bg-gray-800 border-t border-slate-200 dark:border-gray-700 flex gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => handleNavigation('profile', '/profile')}
                className="flex-1 py-2.5 px-3 text-xs font-bold text-slate-700 dark:text-gray-200 bg-slate-100 dark:bg-gray-700 hover:bg-slate-200 dark:hover:bg-gray-600 rounded-xl flex items-center justify-center gap-1.5 transition-colors"
              >
                <User className="w-4 h-4 text-indigo-600" />
                Profil Ayarları
              </button>

              <button
                type="button"
                onClick={() => { setShowFullMenu(false); logout(); }}
                className="flex-1 py-2.5 px-3 text-xs font-bold text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/60 rounded-xl flex items-center justify-center gap-1.5 transition-colors border border-rose-200 dark:border-rose-800"
              >
                <LogOut className="w-4 h-4" />
                Çıkış Yap
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default MobileNavigation;